import React, { useEffect, useRef } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useClickAway } from 'react-use';
import { NavLink } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimesCircle, faSignIn, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { menuSideBarAtom, userAtom } from '../../../atoms/Atoms';
import { backdropVariants, sidebarVariants } from '../../../utils';
import Logo from '../logo/Logo';
import LanguagePicker from '../language-picker/LanguagePicker';
import AuthenticatedSideBar from './shared-components/AuthenticatedSideBar';

function MenuSideBar() {
  const { t } = useTranslation();
  const ref = useRef();
  const user = useRecoilValue(userAtom);
  const [isMenuOpen, setIsMenuOpen] = useRecoilState(menuSideBarAtom);

  useClickAway(ref, () => {
    setIsMenuOpen(false);
  });

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  return (
    <div>
      <AnimatePresence key="msb">
        {isMenuOpen && (
          <motion.div
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="fixed bg-gray-800 inset-0 bg-opacity-75 z-40"
          >
            <motion.div
              ref={ref}
              variants={sidebarVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              className="absolute right-0 h-full flex flex-col w-full md:w-96 bg-sidebar shadow-pop"
            >
              <div className="flex items-center justify-between gap-2 border-b border-line px-4 py-4">
                <button
                  aria-label="home menu"
                  type="button"
                  className="transition-opacity duration-200 ease-in-out hover:opacity-75 outline-none focus:outline-none"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Logo className="w-32" />
                </button>
                <div className="flex items-center gap-1">
                  <LanguagePicker />
                  <button
                    aria-label="close menu"
                    type="button"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex h-10 w-10 items-center justify-center rounded-lg text-xl text-muted transition-colors duration-200 ease-in-out hover:bg-gold-50 hover:text-ink outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-400"
                  >
                    <FontAwesomeIcon icon={faTimesCircle} />
                  </button>
                </div>
              </div>
              {user && <AuthenticatedSideBar />}
              {!user && (
                <div className="py-2">
                  <NavLink
                    to="/"
                    onClick={() => setIsMenuOpen(false)}
                    className={({ isActive }) => `${isActive ? 'bg-sidebarSelected text-sidebarSelectedText font-semibold ' : 'text-sidebarText '}mx-2 my-0.5 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm tracking-tight transition-colors duration-200 ease-in-out hover:bg-gold-50 hover:text-sidebarSelectedText ring-0 outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-400`}
                  >
                    <FontAwesomeIcon icon={faSignIn} />
                    <div>{t('sign_in')}</div>
                  </NavLink>
                  <NavLink
                    to="/register"
                    onClick={() => setIsMenuOpen(false)}
                    className={({ isActive }) => `${isActive ? 'bg-sidebarSelected text-sidebarSelectedText font-semibold ' : 'text-sidebarText '}mx-2 my-0.5 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm tracking-tight transition-colors duration-200 ease-in-out hover:bg-gold-50 hover:text-sidebarSelectedText ring-0 outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-400`}
                  >
                    <FontAwesomeIcon icon={faUserPlus} />
                    <div>{t('create_account')}</div>
                  </NavLink>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default MenuSideBar;
